import { useCallback, useMemo } from 'react';

import type { ScheduledTransactionEditSeriesType } from 'app-components/flux/scheduled-transactions/scheduledTransactionEditForm';

import {
  buildScheduledTransactionEditFormSetters,
  type ScheduledTransactionEditDialogFormValues,
  type ScheduledTransactionEditFormAccessors,
  type ScheduledTransactionEditFormFields,
} from '../types';
import { scheduledTransactionEditFormHooks } from './formHooks';

type Params = {
  onFieldChange?: (path: string) => void;
};

export function useScheduledTransactionEditFormFields({
  onFieldChange,
}: Params = {}): ScheduledTransactionEditFormFields {
  const manager = scheduledTransactionEditFormHooks.useManager();
  const getValues = useCallback(
    () => manager.runSelector(({ state }) => state.values) as unknown as ScheduledTransactionEditDialogFormValues,
    [manager],
  );

  const setFieldValue = useCallback(
    (path: string, value: any) => {
      manager.fireAction('setFieldValue', { path, value });
      onFieldChange?.(path);
      return Promise.resolve();
    },
    [manager, onFieldChange],
  );

  const setSeriesType = useCallback(
    (nextType: ScheduledTransactionEditSeriesType) => {
      manager.fireAction('setSeriesType', { nextType });
      onFieldChange?.('transaction');
    },
    [manager, onFieldChange],
  );

  const setters = useMemo(
    () => buildScheduledTransactionEditFormSetters(setFieldValue, setSeriesType),
    [setFieldValue, setSeriesType],
  );

  return useMemo(
    () => ({
      get values() {
        return getValues();
      },
      setters,
      derived: {
        get dirty() {
          return manager.isFormDirty();
        },
        get transaction() {
          return getValues().transaction;
        },
      },
    }),
    [getValues, manager, setters],
  );
}

export function useScheduledTransactionEditFormValues(): ScheduledTransactionEditDialogFormValues {
  return scheduledTransactionEditFormHooks.useSelector(
    ({ state }) => state.values,
  ) as ScheduledTransactionEditDialogFormValues;
}

export function useScheduledTransactionEditFormAccessors(
  form: ScheduledTransactionEditFormAccessors,
): ScheduledTransactionEditFormAccessors {
  const values = useScheduledTransactionEditFormValues();

  return useMemo(
    () => ({
      ...form,
      values,
      derived: {
        ...form.derived,
        transaction: values.transaction,
      },
    }),
    [form, values],
  );
}
